import { type FC, useContext, Suspense, useRef } from 'react';
import classnames from 'classnames';
import SV from 'server-vars';

import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary.tsx';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer.tsx';
import { ModalSpinner } from 'dibs-elements/exports/ModalSpinner.tsx';
import { GetHelpZendeskModalRenderer } from './GetHelpZendeskModalRenderer.tsx';
import { GetHelpZendeskError } from './GetHelpZendeskError.tsx';

import { localStorage } from 'dibs-browser-storage';
import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { GET_HELP_MODAL_ZENDESK_OPTIONS } from './GetHelpZendeskConstants.ts';
import { trackGetHelpZendeskEvent } from './getHelpZendeskTracking.ts';
import { getGetHelpZendeskLocalStorageState } from './getHelpZendeskHelpers.ts';

import dibsCss from 'dibs-css';
import styles from './GetHelpZendeskModal.module.css';

// 2 hours
const MODAL_EXPIRATION_INTERVAL = 7200000;

const isBuyerDesktop = SV.get('settings.isDesktop');
const isSellerDesktop = SV.get('settings.deviceType') === 'desktop';

const GetHelpZendeskModal: FC = () => {
    const { state } = useContext(GetHelpZendeskContext);
    const dragHandle = useRef<HTMLDivElement>(null);
    const {
        isOpen,
        onClose,
        orderId,
        step,
        articleId,
        stepsPath,
        isSeller,
        contactSupportSearchQuery,
        categoryId,
        topicKey,
        itemId,
    } = state;

    const getHelpModalZendesk = getGetHelpZendeskLocalStorageState();

    const presetPositionX = getHelpModalZendesk?.positionX;
    const presetPositionY = getHelpModalZendesk?.positionY;

    const isDesktop = isBuyerDesktop || isSellerDesktop;

    const onModalOpen = (): void => {
        if (!getHelpModalZendesk?.isOpen) {
            localStorage.setItem(GET_HELP_MODAL_ZENDESK_OPTIONS, {
                isOpen: true,
                timestamp: Date.now(),
                orderId,
                step,
                articleId,
                stepsPath,
                contactSupportSearchQuery,
                categoryId,
                topicKey,
                itemId,
            });
        } else {
            trackGetHelpZendeskEvent({
                stepInteractionName: 'Navigation With Get Help Modal Opened',
                trigger: 'Log the clicked navigation link',
                isSeller,
            });

            const modalTimestamp = getHelpModalZendesk?.timestamp;

            if (!modalTimestamp) {
                return;
            }

            if (Date.now() - modalTimestamp > MODAL_EXPIRATION_INTERVAL) {
                onClose({ isClosedAutomatically: true });
            }
        }
    };

    const setPosition = (x: number, y: number): void => {
        localStorage.setItem(GET_HELP_MODAL_ZENDESK_OPTIONS, {
            isOpen: true,
            positionX: x,
            positionY: y,
        });

        trackGetHelpZendeskEvent({
            stepInteractionName: 'Get Help Modal Dragged',
            isSeller,
        });
    };

    return (
        <ModalContainer
            dataTn="get-help-zendesk-modal"
            isOpen={isOpen}
            onClose={() => onClose()}
            modalPosition="rightTop"
            closeOnOutsideClick={false}
            {...(isDesktop && {
                modalClass: classnames(styles.modal, dibsCss.roundedXxsmall),
                preventBackScroll: false,
                removeOverlay: true,
                onOpen: onModalOpen,
                draggableOptions: {
                    isDraggable: true,
                    dragHandleRef: dragHandle,
                    presetPositionX,
                    presetPositionY,
                    setPosition,
                },
            })}
            {...(!isDesktop && {
                modalClass: classnames(dibsCss.flex, dibsCss.flexCol),
            })}
        >
            {isDesktop && (
                <div
                    ref={dragHandle}
                    className={classnames(
                        styles.dragArea,
                        dibsCss.wFull,
                        dibsCss.hMedium,
                        dibsCss.sticky,
                        dibsCss.top0,
                        dibsCss.zLow
                    )}
                />
            )}
            <ErrorBoundary fallback={() => <GetHelpZendeskError renderOutsideContent />}>
                <Suspense fallback={<ModalSpinner isOpen />}>
                    <GetHelpZendeskModalRenderer isDesktop={isDesktop} />
                </Suspense>
            </ErrorBoundary>
        </ModalContainer>
    );
};

export { GetHelpZendeskModal };
